/**
 * CARTO basemap API key — resolved at runtime, from two sources.
 *
 * CARTO began watermarking keyless basemap tiles ("API KEY REQUIRED" painted
 * into the PNG) in 2026. The tiles still return HTTP 200, so there is no error
 * for the app to catch — the only fix is to send a key.
 *
 * Unlike the AI provider keys, this is a PUBLIC key: it rides in the tile URL
 * and is visible to anyone who views source. It must be domain-restricted at
 * CARTO's end, not hidden here. That is why it does not live behind the
 * cloud-tenant `apiKeys` blanking rule in `organizations/get-me.ts`.
 *
 * Two sources, in precedence order:
 *
 *  1. `Organization.settings.apiKeys.carto` — an on-prem operator's own key,
 *     entered in Settings → Utilities. Set via {@link setCartoApiKey} when
 *     branding loads. On cloud this is always absent for tenants (get-me
 *     blanks `apiKeys` for every `ctx.tenantId !== null`), which is
 *     deliberate: cloud tenants fall through to source 2.
 *  2. `/config.json`'s `cartoApiKey` — the per-deployment operator key,
 *     rendered by the `Generate Cognito Config` step in deploy.yml. Set via
 *     {@link setDeploymentCartoApiKey} at boot, before React mounts.
 *
 * A module singleton rather than React state because `getPresetById()` is
 * called at module scope (see `pages/forms/components/SubmissionGeometryPreview.tsx`),
 * where no hook value is reachable. Mirrors the `setRuntimeApiBaseUrl`
 * pattern in `config/env.ts`.
 *
 * With no key from either source both getters return `''` and the tile
 * templates are emitted unchanged — today's behaviour, watermark included.
 */

let deploymentCartoApiKey = ''
let orgCartoApiKey = ''

/** Set the per-deployment key from /config.json. Called by loadAuthConfig at boot. */
export function setDeploymentCartoApiKey(key: string): void {
  deploymentCartoApiKey = key.trim()
}

/**
 * Set the org-level override. Called by `BrandingProvider` on every render, so
 * it must tolerate being called with the same value repeatedly and with `null`
 * (no override configured / branding not loaded yet) without clobbering the
 * deployment key.
 */
export function setCartoApiKey(key: string | null): void {
  orgCartoApiKey = (key ?? '').trim()
}

/** The effective key: org override wins, deployment key otherwise, else `''`. */
export function getCartoApiKey(): string {
  return orgCartoApiKey || deploymentCartoApiKey
}

/**
 * Append `?key=…` to a CARTO tile or thumbnail URL.
 *
 * The parameter is `key`, NOT `api_key`. This is not cosmetic and cannot be
 * caught by inspecting a status code: an unrecognised parameter is silently
 * ignored and the tile comes back 200 with the watermark still painted in,
 * indistinguishable from sending no key at all. Verified live against
 * basemaps.cartocdn.com — a real key under `key=` returns a clean 31,072-byte
 * tile where the same key under `api_key=` returned the 28,115-byte
 * watermarked one, byte-identical to a deliberately bogus key.
 *
 * Returns `url` untouched when no key is configured, so a deployment without
 * one behaves exactly as it did before this existed. Uses a literal `?` rather
 * than `URL` because the tile templates carry unencoded `{z}/{x}/{y}` and
 * `{subDomain}` placeholders that `URL` would percent-encode into breakage.
 */
export function withCartoApiKey(url: string): string {
  const key = getCartoApiKey()

  if (!key) return url

  return `${url}${url.includes('?') ? '&' : '?'}key=${encodeURIComponent(key)}`
}
